/**
 * Тема оформления (18-profile-parameters.md §7, §11.1).
 *
 * Различаем **выбор** и **тему**: человек выбирает «тёмная», «светлая» или
 * «системная», а покрасить экран можно только в тёмный или светлый. Выбор
 * хранится на сервере — он принадлежит человеку и должен переезжать вместе с
 * ним на другое устройство; тема вычисляется здесь и сейчас.
 *
 * Профиль едет по сети, а красить надо до первого кадра, иначе светлая тема
 * открывается вспышкой чёрного. Поэтому выбор дублируется в `localStorage` и
 * применяется на старте, а ответ сервера его уточняет.
 */

import { onTelegramEvent, setWindowColor, tg } from "./telegram";

export type ThemeChoice = "dark" | "light" | "system";
export type Theme = "dark" | "light";

const KEY = "rack.theme";

/** Фон окна Telegram под каждую тему. Совпадает с `--bg` в index.css. */
export const THEME_BG: Record<Theme, string> = {
  dark: "#101216",
  light: "#edf1fb",
};

export function isChoice(value: unknown): value is ThemeChoice {
  return value === "dark" || value === "light" || value === "system";
}

/**
 * Какой выбор показывать.
 *
 * Сервер главнее кэша, кэш — главнее умолчания: между стартом и ответом
 * `/me` человек видит то, что выбрал в прошлый раз.
 */
export function pickChoice(fromServer: unknown, cached: unknown): ThemeChoice {
  if (isChoice(fromServer)) return fromServer;
  if (isChoice(cached)) return cached;
  return "dark";
}

/** Во что красить экран при этом выборе и этой теме телефона. */
export function resolveTheme(choice: ThemeChoice, systemDark: boolean): Theme {
  if (choice === "system") return systemDark ? "dark" : "light";
  return choice;
}

/**
 * Тёмная ли тема снаружи.
 *
 * Сначала спрашиваем Telegram: внутри Mini App его тема вернее системной —
 * человек мог поставить тёмный Telegram на светлом телефоне. Нет ответа —
 * смотрим на систему.
 */
export function systemIsDark(): boolean {
  const scheme = tg()?.colorScheme;
  if (scheme === "dark" || scheme === "light") return scheme === "dark";
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? true;
}

/** Выбор из кэша. Приватный режим и запрет хранилища — не ошибка, а «не знаем». */
export function cachedChoice(): ThemeChoice | null {
  try {
    const value = localStorage.getItem(KEY);
    return isChoice(value) ? value : null;
  } catch {
    return null;
  }
}

export function rememberChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(KEY, choice);
  } catch {
    // Хранилище недоступно — выбор доедет с профилем, просто чуть позже.
  }
}

/**
 * Покрасить документ и окно Telegram.
 *
 * `data-theme` на `<html>`: светлые токены объявлены под этим атрибутом, и
 * смена темы не требует перерисовки чего-либо руками.
 */
export function applyTheme(choice: ThemeChoice): void {
  const theme = resolveTheme(choice, systemIsDark());
  document.documentElement.dataset.theme = theme;
  setWindowColor(THEME_BG[theme]);
}

/**
 * Следить за темой снаружи, пока выбрана «системная».
 *
 * Без этого настройка работала бы только до перезапуска: человек переключает
 * телефон на ночной режим, а приложение остаётся светлым. Возвращает отписку.
 */
export function watchSystemTheme(onChange: () => void): () => void {
  const off = onTelegramEvent("themeChanged", onChange);
  const media = window.matchMedia?.("(prefers-color-scheme: dark)");
  media?.addEventListener?.("change", onChange);
  return () => {
    off();
    media?.removeEventListener?.("change", onChange);
  };
}
